import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGE_CODES, type LanguageCode } from './languages';

// Type guard: narrows an arbitrary value to a supported LanguageCode by checking
// membership in a code list. Used for both the stored preference and the server-supplied
// customer language. `supported` defaults to the global registry but callers should pass
// their app's available set (so a language another app ships is not treated as valid here).
export function isSupportedLanguage(
  value: unknown,
  supported: readonly string[] = SUPPORTED_LANGUAGE_CODES,
): value is LanguageCode {
  return typeof value === 'string' && supported.includes(value);
}

// Default-language detection, implemented once and reused everywhere. Precedence: an
// explicit stored preference, then the browser's language list, then the default. Pure:
// the caller reads localStorage and passes `stored` in. `supported` is the set to match
// against — pass the app's availableLanguages so detection never resolves to a language
// the app cannot load (it defaults to the global registry for standalone callers).
export function detectLanguage(
  stored?: string | null,
  supported: readonly LanguageCode[] = SUPPORTED_LANGUAGE_CODES,
): LanguageCode {
  // 1. An explicit, still-supported stored preference always wins.
  if (isSupportedLanguage(stored, supported)) return stored;

  // 2. Walk the browser's preferred languages, most-preferred first. This is a
  // client-only SPA, so `navigator` is always present; prefer the ordered list and fall
  // back to the single primary language.
  const browserTags = navigator.languages?.length ? navigator.languages : [navigator.language];

  for (const tag of browserTags) {
    if (!tag) continue;
    const lowerTag = tag.toLowerCase();

    // 2a. Try a full case-insensitive match first (so 'pt-br' matches 'pt-BR').
    const fullMatch = supported.find((code) => code.toLowerCase() === lowerTag);
    if (fullMatch) return fullMatch;

    // 2b. Fall back to matching base subtags (so 'en-US' -> 'en', 'es-419' -> 'es',
    // 'pt' -> 'pt-BR').
    const baseTag = lowerTag.split('-')[0];
    const baseMatch = supported.find((code) => code.toLowerCase().split('-')[0] === baseTag);
    if (baseMatch) return baseMatch;
  }

  // 3. Nothing matched.
  return DEFAULT_LANGUAGE;
}
